// hooks/use-notes.js — NOTES DATA HOOK
// ═══════════════════════════════════════════════════════════
// Single source of truth for loading + mutating notes. Wraps
// /api/user/notes with auth headers and returns a tidy surface
// for features that need notes.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { shouldGenerateSummary, generateAndAttachSummary } from '../lib/note-summary';

export const NOTE_TYPE_COLORS = {
  note:      '#c8a96e', // gold
  voice:     '#7a9e82', // sage
  idea:      '#9b8fc4', // purple
  highlight: '#c46050', // terracotta
};

export const NOTE_TYPE_LABELS = {
  note:      'Note',
  voice:     'Voice',
  idea:      'Idea',
  highlight: 'Highlight',
};

export function useNotes({ userId, supabase }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session?.access_token || ''}`,
    };
  }, [supabase]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch('/api/user/notes', { headers });
      if (res.status === 401) { setNotes([]); return; } // not authenticated yet
      if (!res.ok) throw new Error(`Load notes failed (${res.status})`);
      const { notes: list } = await res.json();
      setNotes(list || []);
    } catch (e) {
      console.error('useNotes.load:', e);
      setError(e.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [userId, authHeaders]);

  useEffect(() => { if (userId) load(); }, [userId, load]);

  async function createNote(payload) {
    const headers = await authHeaders();
    const res = await fetch('/api/user/notes', {
      method: 'POST', headers, body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const { error: errMsg } = await res.json().catch(() => ({}));
      throw new Error(errMsg || 'Create failed');
    }
    const { note } = await res.json();
    setNotes(prev => [note, ...prev]);

    // Fire-and-forget Haiku summary for types that benefit. When it
    // lands the card re-renders with the quality description instead
    // of the sentence-sliced excerpt.
    if (shouldGenerateSummary(note)) {
      generateAndAttachSummary(note, {
        supabase,
        onUpdated: (updated) => {
          setNotes(prev => prev.map(n => n.id === updated.id ? updated : n));
        },
      });
    }

    return note;
  }

  async function updateNote(id, patch) {
    const headers = await authHeaders();
    const res = await fetch('/api/user/notes', {
      method: 'PATCH', headers, body: JSON.stringify({ id, ...patch }),
    });
    if (!res.ok) {
      const { error: errMsg } = await res.json().catch(() => ({}));
      throw new Error(errMsg || 'Update failed');
    }
    const { note } = await res.json();
    setNotes(prev => prev.map(n => (n.id === id ? note : n)));
    return note;
  }

  async function archiveNote(id) {
    setNotes(prev => prev.filter(n => n.id !== id));
    try {
      const headers = await authHeaders();
      await fetch(`/api/user/notes?id=${encodeURIComponent(id)}`, { method: 'DELETE', headers });
    } catch (e) { console.error('archiveNote:', e); }
  }

  return {
    notes, loading, error,
    refresh: load,
    createNote, updateNote, archiveNote,
  };
}
